import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, Loader2, RefreshCw, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import { formatWorkflowDate } from '../../utils/workflowInsightsApi';
import {
  fetchTriggerEnrollmentStats,
  fetchTriggerUnmatchReasons,
} from '../../utils/workflowTriggerStatsApi';
import { EMPTY_TRIGGER_STATS, getTriggerTitle } from '../../utils/workflowTriggerUtils';

const defaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  const fmt = (date) => date.toISOString().slice(0, 10);
  return { start: fmt(start), end: fmt(end) };
};

const MODAL_WIDTH = 'min(1240px, calc(100vw - 96px))';

const WorkflowTriggerStatsModal = ({
  isOpen,
  onClose,
  trigger,
  token,
  workflowId,
  agencyId,
}) => {
  const [activeTab, setActiveTab] = useState('data');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(EMPTY_TRIGGER_STATS);
  const [unmatchReasons, setUnmatchReasons] = useState([]);
  const [contactId, setContactId] = useState('');
  const [{ start, end }, setDateRange] = useState(defaultDateRange);

  const title = getTriggerTitle(trigger);

  const loadStats = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    try {
      const data = await fetchTriggerEnrollmentStats({
        token,
        workflowId,
        agencyId,
        triggerId: trigger?.id,
        startDate: start,
        endDate: end,
        contactId: contactId || undefined,
        page,
        pageSize,
      });
      setStats(data || EMPTY_TRIGGER_STATS);
    } finally {
      setLoading(false);
    }
  }, [isOpen, token, workflowId, agencyId, trigger?.id, start, end, contactId, page, pageSize]);

  const loadUnmatchReasons = useCallback(async () => {
    if (!isOpen || activeTab !== 'reasons') return;
    const reasons = await fetchTriggerUnmatchReasons();
    setUnmatchReasons(reasons);
  }, [isOpen, activeTab]);

  useEffect(() => {
    if (!isOpen) return;
    loadStats();
  }, [isOpen, loadStats]);

  useEffect(() => {
    loadUnmatchReasons();
  }, [loadUnmatchReasons]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((stats.total || 0) / pageSize)),
    [stats.total, pageSize],
  );

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center px-6 py-8 sm:px-12 md:px-[48px] md:py-8"
      data-testid="workflow-trigger-stats-modal"
      data-testid-overlay="workflow-trigger-stats-modal-overlay"
      data-workflow-editor-ignore-pan=""
      role="dialog"
      aria-modal="true"
      style={{ background: 'rgba(15, 23, 42, 0.60)' }}
      onClick={onClose}
    >
      <div
        className="absolute inset-0"
        aria-hidden="true"
        data-testid="workflow-trigger-stats-modal-backdrop"
      />
      <div
        className="relative z-10 flex max-h-[calc(100vh-64px)] min-w-0 flex-col overflow-hidden rounded-[14px] bg-white shadow-[0_30px_80px_rgba(15,23,42,0.28)] max-sm:mt-3 max-sm:max-h-[calc(100vh-24px)] max-sm:w-[calc(100vw-24px)] sm:w-[calc(100vw-48px)] md:w-[min(1240px,calc(100vw-96px))] md:min-w-[960px]"
        data-testid="workflow-trigger-stats-modal-panel"
        data-modal-width={MODAL_WIDTH}
        style={{ width: MODAL_WIDTH }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">
            Stats: {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            data-testid="workflow-trigger-stats-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="shrink-0 border-b border-slate-100 px-6 py-4">
          <div className="flex flex-wrap gap-4">
            {[
              { label: 'Attempted', value: stats.attempted },
              {
                label: 'Matched',
                value: stats.matched,
                badge: `${stats.matchedRate.toFixed(2)}%`,
              },
              { label: 'Unmatched', value: stats.unmatched },
            ].map((item) => (
              <div
                key={item.label}
                className="w-[160px] shrink-0 rounded-xl border border-slate-200 px-4 py-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    {item.label}
                  </div>
                  {item.badge ? (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                      {item.badge}
                    </span>
                  ) : null}
                </div>
                <div className="mt-1 text-3xl font-semibold text-slate-900">{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-slate-100 px-6 py-4">
          <div className="flex w-[280px] shrink-0 items-center gap-2">
            <Input
              type="date"
              value={start}
              onChange={(event) => setDateRange((prev) => ({ ...prev, start: event.target.value }))}
            />
            <span className="text-slate-400">→</span>
            <Input
              type="date"
              value={end}
              onChange={(event) => setDateRange((prev) => ({ ...prev, end: event.target.value }))}
            />
          </div>
          <Input
            value={contactId}
            onChange={(event) => setContactId(event.target.value)}
            placeholder="Select contact"
            className="w-[220px] shrink-0"
          />
          <div className="ml-auto">
            <Button variant="outline" size="icon" onClick={loadStats} disabled={loading}>
              {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            </Button>
          </div>
        </div>

        <div className="flex shrink-0 gap-6 border-b border-slate-100 px-6">
          <button
            type="button"
            onClick={() => setActiveTab('data')}
            className={cn(
              'border-b-2 px-1 py-3 text-sm font-medium',
              activeTab === 'data' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-slate-500',
            )}
          >
            Stats Data
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('reasons')}
            className={cn(
              'border-b-2 px-1 py-3 text-sm font-medium',
              activeTab === 'reasons' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-slate-500',
            )}
          >
            Top Reasons for Unmatch
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {activeTab === 'data' ? (
            stats.rows.length ? (
              <div
                className="w-full overflow-x-auto"
                data-testid="workflow-trigger-stats-table-container"
              >
                <table
                  className="w-full min-w-[900px] text-sm"
                  data-testid="workflow-trigger-stats-table"
                >
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-500">
                      <th className="px-4 py-2.5 font-medium">Name</th>
                      <th className="px-4 py-2.5 font-medium">Email address</th>
                      <th className="px-4 py-2.5 font-medium">Date/Time</th>
                      <th className="px-4 py-2.5 font-medium">Match status</th>
                      <th className="px-4 py-2.5 font-medium">Reason for Unmatch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.rows.map((row) => (
                      <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">
                          <span className="inline-flex items-center gap-1">
                            {row.contactName}
                            <ExternalLink size={12} className="text-slate-400" />
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{row.email}</td>
                        <td className="px-4 py-3 text-slate-600">{formatWorkflowDate(row.dateTime)}</td>
                        <td className="px-4 py-3">
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                            Matched
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-500">{row.reasonForUnmatch}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex min-h-[220px] items-center justify-center text-sm text-slate-500">
                No enrollment data for this trigger yet.
              </div>
            )
          ) : (
            <div className="space-y-2">
              {unmatchReasons.length ? unmatchReasons.map((reason) => (
                <div key={reason.id || reason.label} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  {reason.label}
                </div>
              )) : (
                <div className="flex min-h-[220px] items-center justify-center text-sm text-slate-500">
                  No unmatch reasons recorded yet.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-6 py-4 text-sm text-slate-500">
          <span>Data is stored for last 30 days</span>
          <div className="flex flex-wrap items-center gap-3">
            <span>Showing page {page}</span>
            <Select value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value))}>
              <SelectTrigger className="w-[110px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 50].map((size) => (
                  <SelectItem key={size} value={String(size)}>{size} / page</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              ← Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next →
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default WorkflowTriggerStatsModal;
